/**
 * Types + formatting utilities shared between the cost popup button and
 * dialog. Live data flows through `procedureCostQueries.ts` (Apollo);
 * this module no longer generates mock rows — both `mockProcedureCostDetails`
 * and `computeCostCents` were removed when the dialog moved to the
 * `procedureCost` resolver (cost is computed server-side per ADR-047).
 *
 * Filename retained as `procedureCostMock` to avoid churning the two
 * importers (`ProcedureCostButton.tsx`, `ProcedureCostDialog.tsx`); the
 * suffix is historical.
 */

export type StaffPresence = 'tracked-here' | 'untracked-assigned';

export interface StaffCost {
  staffId: string;
  name: string;
  role: string;
  hourlyRate: number;
  presence: StaffPresence;
  originalDurationSeconds: number;
  effectiveDurationSeconds: number;
  costCents: number | null;
  isManuallyEdited: boolean;
}

export interface SeedStaff {
  staffId: string;
  name: string;
  role: string;
  hourlyRate: number | null;
}

export interface ProcedureCostDetailsInput {
  procedureId: string;
  procedureType: string;
  surgeonName: string;
  patientName: string;
  patientAge: number | null;
  patientSex: string | null;
  orRoomName: string;
  date: string;
  seedStaff?: SeedStaff[];
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

// Renders null as em-dash so the table cell keeps its width when the
// server returns `costCents = null` (rate missing or showStaffCost=false).
export function formatCost(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return currencyFormatter.format(cents / 100);
}

export function formatDuration(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function durationSecondsFromMinutes(minutes: number): number {
  return Math.max(0, Math.round(minutes)) * 60;
}

export function durationMinutesFromSeconds(seconds: number): number {
  return Math.round(seconds / 60);
}
